import { useState, useEffect } from 'react'
import { useGetWorkouts } from 'redux/customHooks/useGetWorkouts'

import ScreenFrame from 'components/Frame/ScreenFrame'
import SearchBar from 'components/Search/SearchBar'
import PaginationController from 'components/Pagination/PaginationController'
import ContentFrame from 'components/Frame/ContentFrame'
import WorkoutListItem from 'components/Workout/WorkoutListItem'
import WorkoutV5ListItem from 'components/Workout/WorkoutV5ListItem'

export default function WorkoutList() {

    const workouts = useGetWorkouts()

    const [searchTerm, setSearchTerm] = useState('')
    const [showFilters, setShowFilters] = useState(false)
    const [filteredWorkouts, setFilteredWorkouts] = useState([])
    const [paginatedWorkouts, setPaginatedWorkouts] = useState([])
    const [pagination, setPagination] = useState({
        page: 1,
        pageSize: 10,
        totalCount: 0,
        totalPages: 0,
        hasNext: false,
        hasPrevious: false
    })

    const FilterWorkoutsBySearchTerm = (item) => {
        const fullName = (item.firstName || '').toLowerCase() + ' ' + (item.lastName || '').toLowerCase()

        return (
            fullName.includes(searchTerm.toLowerCase()) ||
            (item.name || '').toLowerCase().includes(searchTerm.toLowerCase())
        )
    }

    const HandlePageSizeChange = (newPageSize) => {
        setPagination(prev => ({
            ...prev,
            pageSize: parseInt(newPageSize),
            page: 1
        }))
    }

    // Update filtered workouts when search term or filters change
    useEffect(() => {
        if (workouts !== null) {
            const filtered = workouts.filter(item => 
                FilterWorkoutsBySearchTerm(item) 
                // && FilterByCoach(item)
                // && FilterByTrainingPeaksIntegrationStatus(item)
            )

            setFilteredWorkouts(filtered)
        }
    }, [workouts, searchTerm])

    // Update pagination and paginated athletes when filteredAthletes or pagination values change
    useEffect(() => {
        if (filteredWorkouts.length > 0) {
            const totalCount = filteredWorkouts.length
            const totalPages = Math.ceil(totalCount / pagination.pageSize) || 1
            const currentPage = Math.min(pagination.page, totalPages || 1)
            
            // Update pagination
            setPagination(prev => ({
                ...prev,
                totalCount,
                totalPages,
                page: currentPage,
                hasNext: currentPage < totalPages,
                hasPrevious: currentPage > 1
            }))

            // Update paginated athletes
            const startIndex = (pagination.page - 1) * pagination.pageSize
            const endIndex = startIndex + pagination.pageSize

            setPaginatedWorkouts(filteredWorkouts.slice(startIndex, endIndex))
        } else {
            setPagination(prev => ({
                ...prev,
                totalCount: 0,
                totalPages: 0,
                page: 1,
                hasNext: false,
                hasPrevious: false
            }))

            setPaginatedWorkouts([])
        }
    }, [filteredWorkouts.length, pagination.page, pagination.pageSize])

    if (workouts === null) { 
        return (
            <ScreenFrame>
                <div className='w-full h-20 bg-gray-200 rounded-lg animate-pulse' />
                <div className='w-full h-[800px] bg-gray-200 rounded-lg animate-pulse' />
            </ScreenFrame>
        )
    }

    return (
        <ScreenFrame>
            <h1 className="text-2xl font-semibold text-gray-900 tracking-tight pt-2 flex-grow">
                Workouts
            </h1>

            <SearchBar 
                placeholder="Enter Workout Name or Athlete Name"
                searchTerm={searchTerm} 
                setSearchTerm={setSearchTerm}
                showFilters={showFilters}
                setShowFilters={setShowFilters}
            />

            {/* Filters */}
            <div>
                <div
                    className='-mb-4 flex gap-4 transition-all duration-200 select-none'
                    style={{
                        maxHeight: showFilters ? '100px' : '0px',
                        opacity: showFilters ? 1 : 0,
                        paddingBottom: showFilters ? '1rem' : '0',
                        pointerEvents: showFilters ? 'auto' : 'none'
                    }}
                >
                    {/* Items per page */}
                    <div className='flex items-center gap-x-1'>
                        <p className='text-sm text-gray-700 '>Items per page:</p>

                        <div className="border border-gray-200 rounded-md shadow-md">
                            <select
                                value={pagination.pageSize}
                                className="px-2 py-1 text-sm border-r-[10px] border-white rounded-md cursor-pointer focus:outline-none"
                                onChange={(e) => HandlePageSizeChange(e.target.value)}
                            >
                                <option value="10">10</option>
                                <option value="25">25</option>
                                <option value="50">50</option>
                                <option value="100">100</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            <PaginationController pagination={pagination} setPagination={setPagination} />

            <ContentFrame className='divide-y divide-gray-200'>
                {paginatedWorkouts.length === 0 &&
                    <div className="text-center text-gray-500 py-4">No workouts found</div>
                }

                {paginatedWorkouts.length > 0 && paginatedWorkouts.map((workout, index) => (
                    workout.boilerplate === true
                        ? <WorkoutV5ListItem key={index} workout={workout} />
                        : <WorkoutListItem key={index} workout={workout} />
                ))}
            </ContentFrame>

            <PaginationController pagination={pagination} setPagination={setPagination} />
        </ScreenFrame>
    )
}
